import { useForm } from "react-hook-form";
import {
  LuSend,
  LuInfo,
  LuBuilding2,
  LuHash,
  LuCalendarDays,
} from "react-icons/lu";
import type { CertifierFormData } from "./useSubmission";
import LoaderButton from "@/components/shared/LoaderButton";

type CertifierFormProps = {
  leaName: string;
  vendorNumber: string;
  certificationCycle: string;
  isSubmitting: boolean;
  isCycleActive: boolean;
  onSubmit: (data: CertifierFormData) => void;
};

export default function CertifierForm({
  leaName,
  vendorNumber,
  certificationCycle,
  isSubmitting,
  isCycleActive,
  onSubmit,
}: CertifierFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<CertifierFormData>({
    mode: "onChange",
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      extension: "",
    },
  });

  const canSubmit = isValid && !isSubmitting && isCycleActive;

  const tooltipMessage = isSubmitting
    ? "Certifying in progress..."
    : !isCycleActive
      ? "Certification is not available outside the certification window"
      : "Fill out all required fields";

  return (
    <div className="certifier-form">
      <div className="certifier-form__banner">
        <div className="d-flex align-items-start gap-3">
          <LuSend
            size={24}
            color="#0f2a3d"
            className="certifier-form__banner-icon"
          />
          <div>
            <h5 className="certifier-form__banner-title">
              Ready for Certification
            </h5>
            <p className="certifier-form__banner-text">
              Please review and confirm the following information before
              submission.
            </p>
          </div>
        </div>
      </div>

      <div className="d-flex gap-3 my-4">
        {[
          { icon: LuBuilding2, label: "LEA Name", value: leaName },
          { icon: LuHash, label: "Vendor Number", value: vendorNumber },
          {
            icon: LuCalendarDays,
            label: "Certification Cycle",
            value: certificationCycle,
          },
        ].map(({ icon: Icon, label, value }) => (
          <div
            key={label}
            className="certifier-form__info-card d-flex align-items-center gap-3 flex-fill"
          >
            <Icon
              size={22}
              color="#64748B"
              className="certifier-form__info-card-icon"
            />
            <div>
              <div className="certifier-form__info-card-label">{label}</div>
              <div className="certifier-form__info-card-value">{value}</div>
            </div>
          </div>
        ))}
      </div>

      {!isCycleActive && (
        <div
          className="d-flex align-items-start gap-3 mb-4"
          role="alert"
          style={{
            backgroundColor: "#fef2f2",
            border: "1px solid #fecaca",
            borderRadius: "0.75rem",
            padding: "1rem 1.25rem",
          }}
        >
          <LuInfo
            size={20}
            color="#991b1b"
            style={{ marginTop: "1px", flexShrink: 0 }}
          />
          <p
            className="mb-0"
            style={{
              color: "#991b1b",
              fontSize: "0.875rem",
              lineHeight: 1.5,
            }}
          >
            Certification is not available outside the certification window.
            Contact your administrator if you believe this is an error.
          </p>
        </div>
      )}

      <hr className="certifier-form__divider" />

      <form onSubmit={handleSubmit(onSubmit)}>
        <h6 className="certifier-form__section-heading">
          Certifier Information
        </h6>

        <div className="row mb-3">
          <div className="col-6">
            <label
              className="form-label certifier-form__label"
              htmlFor="firstName"
            >
              First Name <span className="certifier-form__required" aria-hidden="true">*</span>
            </label>
            <input
              id="firstName"
              type="text"
              className={`form-control certifier-form__input ${errors.firstName ? "is-invalid" : ""}`}
              placeholder="Enter first name"
              aria-required="true"
              disabled={isSubmitting || !isCycleActive}
              {...register("firstName", {
                required: "First name is required",
              })}
            />
            {errors.firstName && (
              <div className="invalid-feedback">{errors.firstName.message}</div>
            )}
          </div>
          <div className="col-6">
            <label
              className="form-label certifier-form__label"
              htmlFor="lastName"
            >
              Last Name <span className="certifier-form__required" aria-hidden="true">*</span>
            </label>
            <input
              id="lastName"
              type="text"
              className={`form-control certifier-form__input ${errors.lastName ? "is-invalid" : ""}`}
              placeholder="Enter last name"
              aria-required="true"
              disabled={isSubmitting || !isCycleActive}
              {...register("lastName", {
                required: "Last name is required",
              })}
            />
            {errors.lastName && (
              <div className="invalid-feedback">{errors.lastName.message}</div>
            )}
          </div>
        </div>

        <div className="row mb-4">
          <div className="col-4">
            <label className="form-label certifier-form__label" htmlFor="email">
              Email <span className="certifier-form__required" aria-hidden="true">*</span>
            </label>
            <input
              id="email"
              type="email"
              className={`form-control certifier-form__input ${errors.email ? "is-invalid" : ""}`}
              placeholder="Enter email address"
              aria-required="true"
              disabled={isSubmitting || !isCycleActive}
              {...register("email", {
                required: "Email is required",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Invalid email address",
                },
              })}
            />
            {errors.email && (
              <div className="invalid-feedback">{errors.email.message}</div>
            )}
          </div>

          <div className="col-4">
            <label
              className="form-label certifier-form__label"
              htmlFor="phoneNumber"
            >
              Phone Number <span className="certifier-form__required" aria-hidden="true">*</span>
            </label>
            <input
              id="phoneNumber"
              type="tel"
              className={`form-control certifier-form__input ${errors.phoneNumber ? "is-invalid" : ""}`}
              placeholder="(555) 555–5555"
              aria-required="true"
              disabled={isSubmitting || !isCycleActive}
              {...register("phoneNumber", {
                required: "Phone number is required",
              })}
            />
            {errors.phoneNumber && (
              <div className="invalid-feedback">
                {errors.phoneNumber.message}
              </div>
            )}
          </div>

          <div className="col-4">
            <label
              className="form-label certifier-form__label"
              htmlFor="extension"
            >
              Extension
            </label>
            <input
              id="extension"
              type="text"
              className="form-control certifier-form__input"
              placeholder="Ext."
              disabled={isSubmitting || !isCycleActive}
              {...register("extension")}
            />
          </div>
        </div>

        <div className="certifier-form__disclaimer d-flex align-items-start gap-3">
          <LuInfo
            size={20}
            color="#a16207"
            className="certifier-form__disclaimer-icon"
          />
          <p className="certifier-form__disclaimer-text mb-0">
            On behalf of the Superintendent of the district and/or Principal, I
            certify that the data submitted is complete, true, and accurate to
            the best of my knowledge.
          </p>
        </div>

        <div className="d-flex justify-content-end">
          <span
            className={`certify-btn-wrapper ${!canSubmit ? "certify-btn-wrapper--disabled" : ""}`}
          >
            {!canSubmit && (
              <span
                id="certifyTooltipId"
                role="tooltip"
                className="certify-btn-tooltip"
              >
                {tooltipMessage}
              </span>
            )}
            <LoaderButton
              type="submit"
              className={`certify-btn d-flex align-items-center gap-2 ${canSubmit ? "certify-btn--enabled" : "certify-btn--disabled"}`}
              disabled={!canSubmit}
              loading={isSubmitting}
              loadingText="Certifying..."
              aria-describedby={!canSubmit ? "certifyTooltipId" : undefined}
            >
              <LuSend size={16} aria-hidden="true" />
              Certify
            </LoaderButton>
          </span>
        </div>
      </form>
    </div>
  );
}
